import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetPopularLists } from "../api-requests/Get";
import { defaultpfp } from "../constants/constants";
import { getListScoreLabel } from "../helpers/ListScoreCalculations";

interface PopularList {
  id: string;
  userName: string;
  userId: string;
  listName: string;
  profileImg: string;
  bgColor: string;
  score: number; // 0-100 scale
  scoreLabel?: string; // Elite/Valid/Debatable/Trash
  totalVotes: number;
  itemsCount: number;
  category: string;
}

const PopularListsSection = () => {
  const navigate = useNavigate();
  const [popularLists, setPopularLists] = useState<PopularList[]>([]);
  const [loading, setLoading] = useState(true);

  // Helper function to get score color based on the 0-100 scale
  const getScoreColor = (score: number) => {
    if (score >= 85) return "text-yellow-400"; // Elite
    if (score >= 70) return "text-green-400"; // Valid
    if (score >= 55) return "text-yellow-600"; // Debatable
    return "text-red-400"; // Trash
  };

  // Helper function to get score label
  const getScoreDisplayLabel = (score: number, scoreLabel?: string) => {
    return scoreLabel || getListScoreLabel(score);
  };

  useEffect(() => {
    const fetchPopularLists = async () => {
      try {
        const response = await GetPopularLists();
        console.log("Popular lists API response:", response);
        if (response.success) {
          console.log("Setting popular lists:", response.lists);
          setPopularLists(response.lists);
        } else {
          console.log("API returned success: false");
        }
      } catch (error) {
        console.error("Error fetching popular lists:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPopularLists();
  }, []);

  const handleListClick = (list: PopularList) => {
    navigate(`/l/${list.id}`);
  };

  return (
    <div className="py-16 px-4 ">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-4">
            🔥 Popular Lists
          </h2>
          <p className="text-gray-400 text-md">
            Community favorites getting the most attention
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {loading ? (
            // Loading skeleton
            Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="bg-gray-700 rounded-lg p-6 animate-pulse"
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gray-600 rounded-full mr-3"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-600 rounded mb-2"></div>
                    <div className="h-3 bg-gray-600 rounded w-2/3"></div>
                  </div>
                </div>
                <div className="h-6 bg-gray-600 rounded mb-4"></div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <div className="h-4 bg-gray-600 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-600 rounded w-1/4"></div>
                  </div>
                  <div className="flex justify-between">
                    <div className="h-4 bg-gray-600 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-600 rounded w-1/4"></div>
                  </div>
                  <div className="h-2 bg-gray-600 rounded"></div>
                </div>
              </div>
            ))
          ) : popularLists.length > 0 ? (
            popularLists.map((list) => (
              <div
                key={list.id}
                className="popular-list-item transform hover:scale-105 cursor-pointer"
                onClick={() => handleListClick(list)}
              >
                {/* User Profile Section */}
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 m-3">
                    {list.profileImg &&
                    list.profileImg !== "/img/profile/default-avatar.png" ? (
                      <img
                        src={list.profileImg}
                        alt={`${list.userName}'s profile`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={defaultpfp}
                        alt={`${list.userName}'s profile`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 p-2">
                    <p className="text-white ">{list.userName}'s</p>
                    <p className="text-white font-bold">{list.listName}</p>
                    {/* Stats */}
                    <div className="space-y-3 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">Score</span>
                        <div className="flex items-center">
                          <span
                            className={`font-bold ${getScoreColor(list.score)}`}
                          >
                            {list.score}
                          </span>
                          <span className="text-gray-400 ml-1">
                            ({getScoreDisplayLabel(list.score, list.scoreLabel)}
                            )
                          </span>
                        </div>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-400">Votes</span>
                        <span className="text-white font-semibold">
                          {list.totalVotes.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            // No lists found
            <div className="col-span-full text-center py-12">
              <p className="text-gray-400">
                No popular lists found at the moment.
              </p>
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className="text-center mt-8">
          {loading ? (
            <div className="h-12 bg-gray-600 rounded-lg w-64 mx-auto animate-pulse"></div>
          ) : (
            <>
              <p className="text-gray-400 mb-4">
                Discover more amazing lists from our community
              </p>
              <button onClick={() => navigate("/lists")} className="btn">
                Create Your List
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PopularListsSection;
